<template>
  <div>
    <v-radio-group row v-model="editorEnabled" v-if="selectPrevious && $can({ task: 'write' })">
      <v-radio label="Select From Previous Tasks" :value="false"></v-radio>
      <v-radio label="Create New Task" :value="true"></v-radio>
    </v-radio-group>
    <template v-if="editorEnabled">
      <!-- A task name is needed only if selectPrevious is enabled -->
      <v-text-field
        v-if="selectPrevious"
        :rules="taskNameRules"
        label="Task Name"
        v-model="localName"
        :readonly="readonly"></v-text-field>
      <json-editor
        v-show="!readonly"
        :schema="schema"
        :value="value"
        :auto-complete="autoComplete"
        :no-validate="noValidate"
        :input-wrapping-class="inputWrappingClass"></json-editor>
    </template>
    <template v-else>
      <v-select
        :rules="$can({ task: 'write' }) ? [v => !!v || 'Please select an existing task, or create a new one'] : []"
        @change="onSelectedExistingTask"
        :items="previousTasks"
        :loading="loading"
        :data-test="`${$attrs['data-test'] ? $attrs['data-test'] + '-' : '' }previous`">
        <template v-slot:item="{ item }">
          <span :data-test-dropdown-option="`${$attrs['data-test'] ? $attrs['data-test'] + '-' : '' }previous-${item.idx}`">
            {{ item.text }}
          </span>
        </template>
      </v-select>
      <json-preview :data="currentConfig" />
    </template>
  </div>
</template>
<script>
import JsonEditor from './vue-json-ui-editor/JsonEditor'

import Select from './Fields/Select'
import TextField from './Fields/TextField'
import Tags from './Fields/Tags'
import Nested from './Fields/Nested'
import Multiple from './Fields/Multiple'
import Checkbox from './Fields/Checkbox'
import { setVal } from './vue-json-ui-editor/utils'
import { fetchPreviousTasks } from '@/services/Tasks'
import JsonPreview from '@/components/JsonPreview'

/**
 *  Props to be passed to the custom components
 */
const componentProps = ({ field, vm }) => ({
  /**
   * The field's name
   */
  label: field.name,
  /**
   * The value of the field in the form
   */
  data: vm.data[field.name],
  /**
   * Method to be called when the value of the field changes
   * (we use setVal to find the right element to update even if it is nested)
   *
   * @param data
   */
  setData: (data) => {
    setVal(vm.value, field.name, data)
  },
  field,
  vm
})

/**
 * Set up custom components that will be used by the form editor
 * This overrides the default elements with our custom Vuetify ones
 */
JsonEditor.setComponent('select', Select, componentProps)
JsonEditor.setComponent('checkbox', Checkbox, componentProps)
JsonEditor.setComponent('text', TextField, componentProps)
JsonEditor.setComponent('integer', TextField, componentProps)
JsonEditor.setComponent('number', TextField, componentProps)
JsonEditor.setComponent('tags', Tags, componentProps)
JsonEditor.setComponent('multiple', Multiple, componentProps)
JsonEditor.setComponent('nested', Nested, componentProps)

export default {
  name: 'TaskJsonEditor',
  props: {
    taskTypeId: { type: Number, default: 0 },
    selectPrevious: { type: Boolean, default: false },
    schema: { type: Object, required: true },
    value: { type: Object, default: () => ({}) },
    autoComplete: { type: String },
    noValidate: { type: Boolean },
    inputWrappingClass: { type: String },
    name: { type: String, default: '' },
    readonly: { type: Boolean, default: false }
  },
  components: { JsonPreview, JsonEditor },
  mounted () {
    // Only select existing tasks if selectPrevious is enabled
    if (this.selectPrevious) {
      this.fetchExistingTasks()
    }
  },
  data () {
    return {
      editorEnabled: !this.selectPrevious,
      previousTasks: [],
      loading: false,
      localName: this.name,
      currentConfig: null
    }
  },
  computed: {
    /**
     * Rules to be applied to the task name field (it must be filled and be unique)
     *
     * @returns {(function(*=): (*|string))[]}
     */
    taskNameRules () {
      return [
        v => !!v || 'A task name is required',
        v => this.isTaskGroupUnique(v) || 'This task name already exists'
      ]
    }
  },
  methods: {
    /**
     * Fetch previously created tasks of this task type
     */
    fetchExistingTasks () {
      if (!this.editorEnabled && this.previousTasks.length === 0) {
        this.loading = true
        fetchPreviousTasks(this.taskTypeId).then((previousTasks) => {
          this.previousTasks = previousTasks.map((task, idx) => ({ value: task.config, text: task.name, idx }))
          this.loading = false
        })
      }
    },
    /**
     * Event called when a previously created task is selected
     *
     * @param taskConfig
     */
    onSelectedExistingTask (taskConfig) {
      this.currentConfig = taskConfig
      this.$emit('selectedExistingTask', this.currentConfig)
    },
    /**
     * Returns true if no task name is specified or the task name is unique
     */
    isTaskGroupUnique (name) {
      const previousTaskNames = new Set(this.previousTasks.map(task => task.text))
      return name.length === 0 || !previousTaskNames.has(name)
    }
  },
  watch: {
    editorEnabled () {
      this.fetchExistingTasks()
    },
    localName (newValue) {
      this.$emit('update:name', newValue)
    },
    name (newValue) {
      if (newValue !== this.localName) {
        this.localName = newValue
      }
    }
  }
}
</script>
